import { emit, listen } from "@tauri-apps/api/event";
import { LogicalPosition, LogicalSize } from "@tauri-apps/api/dpi";
import { availableMonitors, currentMonitor } from "@tauri-apps/api/window";
import { WebviewWindow } from "@tauri-apps/api/webviewWindow";
import { isDesktopApp } from "@/lib/runtime";
import {
  PRESENTER_CONTROL,
  PRESENTER_EVENT,
  PRESENTER_LABEL,
  PRESENTER_READY,
  type PresenterControl,
  type PresenterState,
} from "@/lib/presenter";

async function placeOnMonitor(window: WebviewWindow, state: PresenterState) {
  const monitors = await availableMonitors();
  const current = await currentMonitor();
  const other = monitors.find((monitor) => monitor.name !== current?.name) ?? null;
  if (!other) return false;

  const position = other.position.toLogical(other.scaleFactor);
  const size = other.size.toLogical(other.scaleFactor);
  await window.setPosition(new LogicalPosition(position.x, position.y));
  await window.setSize(new LogicalSize(size.width, size.height));
  await window.setFullscreen(true);
  await window.show();
  await emit(PRESENTER_EVENT, state);
  return true;
}

/**
 * Put the deck on the other display when there is one.
 *
 * Returns false when the laptop is the only screen, so the caller can fall
 * back to covering this window. Opening a second window on a single display
 * would hide the manuscript, which is the thing the preacher is reading from.
 */
export async function openPresenterWindow(state: PresenterState): Promise<boolean> {
  if (!isDesktopApp) return false;

  const monitors = await availableMonitors();
  if (monitors.length < 2) return false;

  const existing = await WebviewWindow.getByLabel(PRESENTER_LABEL);
  if (existing) return placeOnMonitor(existing, state);

  const presenter = new WebviewWindow(PRESENTER_LABEL, {
    url: "/?surface=presenter",
    title: "Sermon Space Presenter",
    decorations: false,
    fullscreen: true,
    resizable: false,
    visible: true,
    focus: false,
  });

  await new Promise<void>((resolve, reject) => {
    void presenter.once("tauri://created", () => resolve());
    void presenter.once("tauri://error", (event) => {
      reject(event.payload ?? new Error("Could not open the presenter window"));
    });
  });

  return placeOnMonitor(presenter, state);
}

export async function pushPresenterState(state: PresenterState) {
  if (!isDesktopApp) return;
  await emit(PRESENTER_EVENT, state);
}

export async function closePresenterWindow() {
  if (!isDesktopApp) return;
  const existing = await WebviewWindow.getByLabel(PRESENTER_LABEL);
  if (existing) await existing.close();
}

export function listenPresenterReady(onReady: () => void) {
  return listen(PRESENTER_READY, () => onReady());
}

export function listenPresenterControl(onControl: (control: PresenterControl) => void) {
  return listen<PresenterControl>(PRESENTER_CONTROL, (event) => onControl(event.payload));
}
